import * as THREE from 'three';
import type { Network } from '../data/types';
import { lineColor } from '../data/lineColors';
import { edgeTo3D, type Vec3 } from '../geometry/paths';

const STRUCTURE_COLORS: Record<string, number> = {
  surface: 0x6f7b8a,
  tunnel: 0x3f6fb0,
  bridge: 0xc9a24a,
};

export class TracksLayer {
  readonly group = new THREE.Group();
  private edgeMeshes: { edgeId: string; lines: string[]; structure: string; material: THREE.MeshStandardMaterial }[] = [];
  private lineColor = new Map<string, THREE.Color>();
  private undergroundGroup = new THREE.Group();

  constructor() {
    this.group.name = 'tracks';
    this.undergroundGroup.name = 'tracks-underground-overlay';
    this.undergroundGroup.visible = false;
    this.group.add(this.undergroundGroup);
  }

  build(network: Network, nodeHeights: Map<string, number>) {
    for (const line of network.lines) {
      this.lineColor.set(line.ref, new THREE.Color(lineColor(line.ref)));
    }
    for (const edge of network.edges) {
      if (edge.coords.length < 2) continue;
      const pts = edgeTo3D(edge, nodeHeights);
      const vec = pts.map((p) => new THREE.Vector3(p.x, p.y, p.z));
      const curve = new THREE.CatmullRomCurve3(vec, false, 'catmullrom', 0.5);
      const segments = Math.max(8, Math.min(200, pts.length * 3));
      const radius = edge.structure === 'tunnel' ? 0.7 : 1.1;
      const geo = new THREE.TubeGeometry(curve, segments, radius, 6, false);
      const base = new THREE.Color(STRUCTURE_COLORS[edge.structure] ?? 0x6f7b8a);
      const mat = new THREE.MeshStandardMaterial({
        color: base,
        emissive: base.clone().multiplyScalar(0.25),
        roughness: 0.7,
        metalness: 0.1,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.userData.edgeId = edge.id;
      this.edgeMeshes.push({ edgeId: edge.id, lines: edge.lines, structure: edge.structure, material: mat });
      this.group.add(mesh);

      // Dashed overlay for underground edges, projected onto the map surface.
      if (edge.structure === 'tunnel') {
        const surfacePts = pts.map((p) => new THREE.Vector3(p.x, 0.5, p.z));
        const lineGeo = new THREE.BufferGeometry().setFromPoints(surfacePts);
        const color = edge.lines.length === 1 ? lineColor(edge.lines[0]) : '#ffffff';
        const lineMat = new THREE.LineDashedMaterial({
          color,
          dashSize: 9,
          gapSize: 6,
          transparent: true,
          opacity: 0.9,
          depthWrite: false,
        });
        const dashed = new THREE.Line(lineGeo, lineMat);
        dashed.computeLineDistances();
        dashed.renderOrder = 6;
        this.undergroundGroup.add(dashed);
      }
    }
  }

  /** Show/hide the dashed underground overlay (tied to the OSM basemap). */
  setUndergroundOverlay(on: boolean) {
    this.undergroundGroup.visible = on;
  }

  /** selected = empty means all lines shown in structure colors */
  setLineSelection(selected: Set<string>) {
    for (const em of this.edgeMeshes) {
      const active = em.lines.filter((l) => selected.has(l));
      const mat = em.material;
      if (!selected.size) {
        const base = STRUCTURE_COLORS[em.structure] ?? 0x6f7b8a;
        mat.color.setHex(base);
        mat.emissive.copy(new THREE.Color(base).multiplyScalar(0.25));
        mat.opacity = 1;
        mat.transparent = false;
      } else if (active.length === 1) {
        const c = this.lineColor.get(active[0]) ?? new THREE.Color(0xffffff);
        mat.color.copy(c);
        mat.emissive.copy(c.clone().multiplyScalar(0.4));
        mat.opacity = 1;
        mat.transparent = false;
      } else if (active.length > 1) {
        mat.color.setHex(0xffffff);
        mat.emissive.setHex(0x555555);
        mat.opacity = 1;
        mat.transparent = false;
      } else {
        mat.color.setHex(0x2a323c);
        mat.emissive.setHex(0x000000);
        mat.opacity = 0.25;
        mat.transparent = true;
      }
    }
  }

}
